import { usePasses } from "../hooks/usePasses";
import { buttonStyles, panelStyle, sectionTitle, statusColor, subtleText, tierColor, tokens } from "../lib/ui";
import type { MembershipTier, UserPass } from "@repo/shared-types";

export function PassPage() {
  const { passes, activeQr, loading, generating, error, generateQr, dismissQr } = usePasses();
  const activePasses = passes.filter((p) => p.is_active);
  const expiredPasses = passes.filter((p) => !p.is_active);

  if (loading) {
    return (
      <div className="page-loading">
        <p style={subtleText}>Loading passes...</p>
      </div>
    );
  }

  return (
    <div className="inner-page__container" style={{ gap: tokens.space.lg }}>
      {/* Hero */}
      <section
        style={{
          ...panelStyle,
          background: "linear-gradient(135deg, #1c1917 0%, #292524 100%)",
          color: "#fef9c3",
          border: "none",
        }}
      >
      <div style={{ fontSize: tokens.fontSize.xs, textTransform: "uppercase", letterSpacing: "0.1em", color: "#fbbf24", fontWeight: tokens.fontWeight.bold }}>
        AekamPass
        </div>
        <h2 style={{ margin: "0.4rem 0", fontSize: tokens.fontSize["3xl"], color: "#fefce8" }}>
          Your passes & venue check-in
        </h2>
        <p style={{ margin: 0, color: "#d6d3d1", fontSize: tokens.fontSize.sm }}>
          Generate a QR token for physical attendance at events, workshops, and retreats.
        </p>
      </section>

      {error && (
        <p style={{ color: tokens.color.error, background: "#fef2f2", borderRadius: tokens.radius.md, padding: tokens.space.sm }}>
          {error}
        </p>
      )}

      {/* Active QR panel */}
      {activeQr && (
        <section style={{ ...panelStyle, border: "1px solid #86efac", background: tokens.color.accentLight }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: tokens.space.sm }}>
            <h3 style={{ ...sectionTitle, color: tokens.color.accent }}>Active check-in token</h3>
            <button onClick={dismissQr} style={buttonStyles.ghost} aria-label="Dismiss QR token">✕</button>
          </div>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: tokens.fontSize.sm,
              wordBreak: "break-all",
              background: "#fff",
              padding: tokens.space.sm,
              borderRadius: tokens.radius.md,
              border: "1px solid #bbf7d0",
              marginBottom: tokens.space.xs,
            }}
          >
            {activeQr.token}
          </div>
          <p style={{ ...subtleText, marginTop: "0.35rem" }}>
            Expires {new Date(activeQr.expires_at).toLocaleString()} — show this to the event staff scanner.
          </p>
        </section>
      )}

      {/* Active passes */}
      <section style={panelStyle}>
        <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm }}>Active Passes ({activePasses.length})</h3>
        {activePasses.length === 0 ? (
        <p style={subtleText}>No active passes found. Contact support or purchase an AekamPass to attend events.</p>
        ) : (
          <div style={{ display: "grid", gap: tokens.space.sm }}>
            {activePasses.map((pass) => (
              <PassCard
                key={pass.id}
                pass={pass}
                isGenerating={generating && activeQr?.passId === pass.id}
                onGenerateQr={() => void generateQr(pass.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Expired passes */}
      {expiredPasses.length > 0 && (
        <section style={panelStyle}>
          <h3 style={{ ...sectionTitle, marginBottom: tokens.space.sm, color: tokens.color.muted }}>
            Expired / Inactive ({expiredPasses.length})
          </h3>
          <div style={{ display: "grid", gap: tokens.space.sm, opacity: 0.65 }}>
            {expiredPasses.map((pass) => (
              <PassCard key={pass.id} pass={pass} isGenerating={false} onGenerateQr={() => {}} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function PassCard({
  pass,
  isGenerating,
  onGenerateQr,
}: {
  pass: UserPass;
  isGenerating: boolean;
  onGenerateQr: () => void;
}) {
  const statusMeta = statusColor(pass.is_active);
  const tierMeta = tierColor(deriveTierFromProductId(pass.product_id));

  return (
    <div
      style={{
        borderRadius: tokens.radius.md,
        padding: tokens.space.sm,
        border: `1px solid ${pass.is_active ? "#86efac" : tokens.color.border}`,
        background: pass.is_active ? "#fafffe" : tokens.color.surfaceAlt,
        display: "grid",
        gap: tokens.space.xs,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: tokens.space.sm }}>
        <div>
          <div style={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.md }}>
            {formatProductId(pass.product_id)}
          </div>
          <div style={{ fontSize: tokens.fontSize.xs, color: tokens.color.muted, marginTop: 2 }}>
            ID: {pass.id.slice(0, 8)}…
          </div>
        </div>
        <div style={{ display: "flex", gap: tokens.space.xs, alignItems: "center" }}>
          <span
            style={{
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              padding: "0.15rem 0.55rem",
              borderRadius: tokens.radius.full,
              background: statusMeta.background,
              color: statusMeta.color,
            }}
          >
            {pass.is_active ? "Active" : "Inactive"}
          </span>
          <span
            style={{
              fontSize: tokens.fontSize.xs,
              fontWeight: tokens.fontWeight.bold,
              padding: "0.15rem 0.55rem",
              borderRadius: tokens.radius.full,
              background: tierMeta.background,
              color: tierMeta.color,
              border: `1px solid ${tierMeta.border}`,
            }}
          >
            {deriveTierFromProductId(pass.product_id)}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: tokens.space.lg, fontSize: tokens.fontSize.sm, color: tokens.color.muted }}>
        <span>Purchased {new Date(pass.purchase_date).toLocaleDateString()}</span>
        {pass.expires_at ? (
          <span>Expires {new Date(pass.expires_at).toLocaleDateString()}</span>
        ) : (
          <span>Never expires</span>
        )}
      </div>

      {pass.is_active && (
        <button
          onClick={onGenerateQr}
          disabled={isGenerating}
          style={{ ...buttonStyles.primary, alignSelf: "flex-start", marginTop: "0.25rem" }}
        >
          {isGenerating ? "Generating…" : "Generate QR Token"}
        </button>
      )}
    </div>
  );
}

/** Heuristic tier inference from product naming conventions. */
function deriveTierFromProductId(productId: string): MembershipTier {
  const id = productId.toLowerCase();
  if (id.includes("vip")) return "vip";
  if (id.includes("elite")) return "elite";
  if (id.includes("premium")) return "premium";
  return "free";
}

function formatProductId(productId: string): string {
  return productId
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
